import { Form, Input, Button, Card, message, Checkbox, Row, Col, Select } from 'antd';
import { UserOutlined, LockOutlined, MailOutlined, TeamOutlined } from '@ant-design/icons';
import { useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useState } from 'react';
import { api } from '../utils/request';
import { useAuthStore } from '../stores/auth';
import LoginBackground from '../components/LoginBackground';
import logoImg from '../assets/kimap-logo.png';

export default function Register() {
  const navigate = useNavigate();
  const { setAuth } = useAuthStore();
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);

  const onFinish = async (values: {
    username: string;
    password: string;
    confirmPassword: string;
    email?: string;
    userRole?: string;
    companyName?: string;
    agreement?: boolean;
  }) => {
    if (values.password !== values.confirmPassword) {
      message.error(t('auth.register.passwordMismatch'));
      return;
    }

    setLoading(true);
    try {
      const res = await api.post<{
        token: string;
        user: any;
      }>('/auth/register', values);

      if (res?.token && res?.user) {
        setAuth(res.token, res.user);
        message.success(t('auth.register.registerSuccess') || '注册成功，正在前往 Kimap...');
        setTimeout(() => navigate('/maps', { replace: true }), 500);
        return;
      }
    } catch (error: any) {
      if (error.code === 409 || error.response?.data?.code === 409) {
        message.error(t('error.conflict'));
      } else {
        message.error(error.response?.data?.message || error.message || t('error.system'));
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-container">
      {/* SVG Background */}
      <LoginBackground />

      {/* Logo */}
      <div className="top-logo">
        <img src={logoImg} alt="Kimap" className="logo-img" />
        <h2>{t('auth.register.title')}</h2>
      </div>

      {/* Register Card */}
      <Card className="auth-card register-card">
        <div className="brand">
          <h1>{t('auth.register.createAccount')}</h1>
          <p className="tagline">{t('auth.register.getStartedSubtitle')}</p>
        </div>

        <Form
          name="register"
          onFinish={onFinish}
          layout="vertical"
          className="auth-form"
          size="large"
        >
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="username"
                label={t('auth.register.username')}
                rules={[
                  { required: true, message: t('auth.register.usernameRequired') },
                  { min: 3, message: t('auth.register.usernameMinLength') },
                ]}
              >
                <Input 
                  prefix={<UserOutlined className="input-icon" />} 
                  placeholder={t('auth.register.usernamePlaceholder')} 
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="email"
                label={t('auth.register.email')}
                rules={[
                  { type: 'email', message: t('auth.register.emailInvalid') },
                ]}
              >
                <Input 
                  prefix={<MailOutlined className="input-icon" />} 
                  placeholder={t('auth.register.emailPlaceholder')} 
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="userRole"
                label={t('auth.register.selectRole')}
              >
                <Select
                  placeholder={t('auth.register.rolePlaceholder')}
                  allowClear
                >
                  <Select.Option value="personal_developer">{t('auth.register.rolePersonalDeveloper')}</Select.Option>
                  <Select.Option value="sales">{t('auth.register.roleSales')}</Select.Option>
                  <Select.Option value="corporate_employee">{t('auth.register.roleCorporateEmployee')}</Select.Option>
                  <Select.Option value="educator">{t('auth.register.roleEducator')}</Select.Option>
                  <Select.Option value="other">{t('auth.register.roleOther')}</Select.Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="companyName"
                label={t('auth.register.companyName')}
              >
                <Input
                  prefix={<TeamOutlined className="input-icon" />}
                  placeholder={t('auth.register.companyNamePlaceholder')}
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="password"
                label={t('auth.register.password')}
                rules={[
                  { required: true, message: t('auth.register.passwordRequired') },
                  { min: 6, message: t('auth.register.passwordMinLength') },
                ]}
              >
                <Input.Password 
                  prefix={<LockOutlined className="input-icon" />} 
                  placeholder={t('auth.register.passwordPlaceholder')} 
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="confirmPassword"
                label={t('auth.register.confirmPassword')}
                rules={[
                  { required: true, message: t('auth.register.confirmPasswordRequired') },
                ]}
              >
                <Input.Password 
                  prefix={<LockOutlined className="input-icon" />} 
                  placeholder={t('auth.register.confirmPasswordPlaceholder')} 
                />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item name="agreement" valuePropName="checked" rules={[
            { required: true, message: t('auth.register.agreementRequired') },
            {
              validator: (_: any, value: boolean) =>
                value ? Promise.resolve() : Promise.reject(t('auth.register.agreementRequired')),
            },
          ]}>
            <Checkbox>
              {t('auth.register.agreement')}
              <a href="#" onClick={(e) => e.preventDefault()}>{t('auth.register.termsOfService')}</a>
              {t('common.and')}
              <a href="#" onClick={(e) => e.preventDefault()}>{t('auth.register.privacyPolicy')}</a>
            </Checkbox>
          </Form.Item>

          <Form.Item>
            <Button type="primary" htmlType="submit" block loading={loading} disabled={loading}>
              {!loading && t('auth.register.createAccountBtn')}
            </Button>
          </Form.Item>
        </Form>

        <div className="auth-footer">
          <span>{t('auth.register.haveAccount')} </span>
          <Link to="/login" className="auth-link">{t('auth.register.signIn')}</Link>
        </div>
      </Card>

      {/* Back to home */}
      <div className="back-home">
        <Link to="/" className="back-link">
          ← {t('auth.register.backToHome')}
        </Link>
        <a className="back-link" href="mailto:kimap.founder@gmail.com">{t('feedback.emailUs', 'Questions? Email us')}</a>
      </div>

      <style>{`
        .auth-container {
          min-height: 100vh;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 24px;
          position: relative;
          overflow-y: auto;
        }

        /* Top logo */
        .top-logo {
          position: fixed;
          top: 32px;
          left: 32px;
          z-index: 10;
          display: flex;
          align-items: center;
          gap: 12px;
        }

        .logo-img {
          height: 22px;
          width: auto;
        }

        .top-logo h2 {
          margin: 0;
          font-size: 18px;
          font-weight: 600;
          color: var(--color-text-primary);
          letter-spacing: -0.5px;
        }

        /* Auth card */
        .auth-card {
          width: 100%;
          max-width: 680px;
          padding: 0;
          position: relative;
          z-index: 10;
          backdrop-filter: blur(10px);
          background: rgba(255, 255, 255, 0.95);
          border: 1px solid var(--color-border);
          margin: 80px 0;
        }

        .auth-card :global(.ant-card-body) {
          padding: 48px 40px;
        }

        /* Brand area */
        .brand {
          text-align: center;
          margin-bottom: 36px;
        }

        .brand h1 {
          font-size: 28px;
          font-weight: 700;
          color: var(--color-text-primary);
          margin: 0 0 8px 0;
          letter-spacing: -0.5px;
        }

        .tagline {
          font-size: 14px;
          color: var(--color-text-secondary);
          margin: 0;
        }

        /* Form */
        .auth-form {
          margin-bottom: 0;
        }

        .auth-form :global(.ant-form-item) {
          margin-bottom: 16px;
        }

        .auth-form :global(.ant-form-item-label > label) {
          font-weight: 500;
          color: var(--color-text-primary);
          font-size: 14px;
          height: 24px;
        }

        .auth-form :global(.ant-input-affix-wrapper),
        .auth-form :global(.ant-input) {
          padding: 10px 12px;
        }

        .input-icon {
          color: var(--color-text-tertiary);
          font-size: 16px;
        }

        .auth-form :global(a) {
          color: var(--color-accent);
          font-weight: 500;
        }

        .auth-form :global(.ant-checkbox-wrapper) {
          font-size: 14px;
          color: var(--color-text-secondary);
        }

        /* Auth footer */
        .auth-footer {
          text-align: center;
          margin-top: 24px;
          padding-top: 24px;
          border-top: 1px solid var(--color-border);
          color: var(--color-text-secondary);
          font-size: 14px;
        }

        .auth-link {
          color: var(--color-accent);
          font-weight: 600;
          margin-left: 4px;
        }

        .auth-link:hover {
          color: var(--color-accent-dark);
        }

        /* Back to home */
        .back-home {
          position: fixed;
          bottom: 32px;
          left: 50%;
          transform: translateX(-50%);
          z-index: 10;
        }

        .back-link {
          display: flex;
          align-items: center;
          gap: 4px;
          color: var(--color-text-secondary);
          font-size: 14px;
          font-weight: 500;
          transition: color 0.2s;
        }

        .back-link:hover {
          color: var(--color-accent);
        }

        /* Responsive */
        @media (max-width: 768px) {
          .auth-card {
            max-width: 100%;
          }
        }

        @media (max-width: 576px) {
          .auth-card :global(.ant-card-body) {
            padding: 32px 24px;
          }

          .brand h1 {
            font-size: 24px;
          }

          .top-logo {
            top: 24px;
            left: 24px;
          }

          .top-logo h2 {
            font-size: 16px;
          }

          .back-home {
            bottom: 24px;
          }

          .auth-form :global(.ant-col) {
            max-width: 100% !important;
            flex: 0 0 100% !important;
          }
        }
      `}</style>
    </div>
  );
}
